<script setup lang="ts">
/**
 * ModifyInitiativeDialog — modal acessível para R4 (modify initiative).
 *
 * HLOS-40 (HLOS-37.3) — Acompanha InitiativeActions. Usuário escreve
 * seu ângulo de exploração; orchestrator registra via modifyInitiative().
 *
 * Acessibilidade (WCAG 2.1 AA):
 * - role="dialog" + aria-modal="true"
 * - aria-labelledby aponta para o título
 * - Foco automático no textarea ao abrir
 * - Esc fecha o dialog
 * - Botão "Enviar" desabilitado com input vazio (zero streak counter)
 *
 * Constitucional:
 * - CC-05: zero penalidade por cancelar
 * - Princípio V (Aprendiz Decide): usuário reformula o ângulo
 */
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";

interface Props {
  open: boolean;
}

interface Emits {
  (e: "submit", userInput: string): void;
  (e: "cancel"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const userInput = ref("");
const inputRef = ref<HTMLTextAreaElement | null>(null);

function onSubmit(): void {
  const v = userInput.value.trim();
  if (v.length === 0) return;
  emit("submit", v);
}

function onCancel(): void {
  emit("cancel");
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape" && props.open) {
    emit("cancel");
  }
}

onMounted(() => {
  document.addEventListener("keydown", onKeydown);
  if (props.open) {
    nextTick(() => inputRef.value?.focus());
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", onKeydown);
});

watch(
  () => props.open,
  (open) => {
    if (open) {
      userInput.value = "";
      nextTick(() => inputRef.value?.focus());
    }
  },
);
</script>

<template>
  <div class="hlos-dialog-backdrop" role="presentation">
    <div
      class="hlos-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hlos-dialog-title"
      data-testid="modify-initiative-dialog"
    >
      <h2 id="hlos-dialog-title" class="hlos-dialog__title">Modificar iniciativa</h2>
      <p class="hlos-dialog__desc">
        Como você quer reformular a provocação? O Companheiro usará seu ângulo na próxima resposta.
      </p>
      <textarea
        ref="inputRef"
        class="hlos-dialog__textarea"
        data-testid="modify-input"
        aria-label="Sua reformulação"
        :value="userInput"
        @input="userInput = ($event.target as HTMLTextAreaElement).value"
        rows="4"
      />
      <div class="hlos-dialog__actions">
        <button
          type="button"
          class="hlos-dialog__btn hlos-dialog__btn--cancel"
          data-testid="modify-cancel"
          @click="onCancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="hlos-dialog__btn hlos-dialog__btn--submit"
          data-testid="modify-submit"
          :disabled="userInput.trim().length === 0"
          @click="onSubmit"
        >
          Enviar
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* HLOS-129 (era HLOS-130.2): Refator para tokens CSS via var(--hlos-color-*).
 * Antes: hex literals hardcoded (#fff, #111, #000 50%, #000 30%) que violavam
 * CC-08 e design system Lume. Depois: tokens centralizados em tokens.css.
 *
 * Constitucional:
 * - CC-08 (sem hardcoded colors): FORTALECIDO via tokens
 * - HLOS-74 (design tokens) — todas cores vêm de var(--hlos-color-*)
 */
.hlos-dialog-backdrop {
  position: fixed;
  inset: 0;
  background: color-mix(in srgb, var(--hlos-color-neutral-900) 50%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.hlos-dialog {
  background: var(--hlos-color-bg);
  color: var(--hlos-color-text);
  padding: 1.5rem;
  border-radius: 0.5rem;
  max-width: 480px;
  width: calc(100vw - 2rem);
  box-shadow: 0 10px 40px color-mix(in srgb, var(--hlos-color-neutral-900) 30%, transparent);
}
.hlos-dialog__title {
  margin: 0 0 0.5rem;
  font-size: 1.1em;
  font-weight: 600;
}
.hlos-dialog__desc {
  margin: 0.5rem 0 1rem;
  font-size: 0.95em;
  opacity: 0.8;
  line-height: 1.4;
}
.hlos-dialog__textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid color-mix(in srgb, currentColor 30%, transparent);
  border-radius: 0.375rem;
  font-family: inherit;
  font-size: 0.95em;
  resize: vertical;
  box-sizing: border-box;
  background: transparent;
  color: inherit;
}
.hlos-dialog__textarea:focus-visible {
  outline: 2px solid color-mix(in srgb, currentColor 60%, transparent);
  outline-offset: 2px;
}
.hlos-dialog__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
}
.hlos-dialog__btn {
  padding: 0.5rem 1rem;
  border: 1px solid color-mix(in srgb, currentColor 30%, transparent);
  background: transparent;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.95em;
}
.hlos-dialog__btn:focus-visible {
  outline: 2px solid color-mix(in srgb, currentColor 60%, transparent);
  outline-offset: 2px;
}
.hlos-dialog__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.hlos-dialog__btn--submit {
  background: color-mix(in srgb, currentColor 12%, transparent);
}
/* No dark-mode override needed: tokens.css handles dark mode via prefers-color-scheme
 * at the root, and var(--hlos-color-bg) / var(--hlos-color-text) auto-resolve.
 */
</style>
